/**
 * Year 10 pH scale model: typical pH ranges of common substances, universal indicator colour bands,
 * acid/base/neutral classification, the ×10-per-pH-unit idea and a particle model of neutralisation.
 */

export type PHClass = 'acidic' | 'neutral' | 'basic';

export type IndicatorColour = 'red' | 'orange' | 'yellow' | 'green' | 'blue' | 'purple';

export interface PHSubstance {
  id: string;
  name: string;
  /** Typical range; real samples vary with brand, dilution and temperature. */
  minPH: number;
  maxPH: number;
  /** A single representative value used for comparisons. */
  typicalPH: number;
  note?: string;
}

export interface IndicatorBand {
  colour: IndicatorColour;
  label: string;
  /** Inclusive lower bound and exclusive upper bound, except the last band which includes 14. */
  fromPH: number;
  toPH: number;
}

export interface PHScaleDataProvenance {
  sources: string[];
  conditions: string;
  reviewStatus: 'pending-teaching-review';
}

export const PH_SCALE_PROVENANCE: PHScaleDataProvenance = {
  sources: [
    'Typical pH values of common substances from standard secondary chemistry texts and the USGS Water Science School pH scale',
    'Neutral pH 7 for pure water at 25 °C (Kw = 1.0 × 10⁻¹⁴, IUPAC / CRC Handbook of Chemistry and Physics)',
    'Universal indicator colour sequence from standard school indicator charts (exact shades vary by brand)',
  ],
  conditions: '25 °C. Values are typical ranges for household and school samples, not measurements.',
  reviewStatus: 'pending-teaching-review',
};

export const NEUTRAL_PH = 7;
const NEUTRAL_TOLERANCE = 0.05;
export const PH_MIN = 0;
export const PH_MAX = 14;
/** Each pH unit is a ten-fold change in hydrogen ion concentration. */
export const PH_UNIT_FACTOR = 10;

export const PH_SUBSTANCES: PHSubstance[] = [
  { id: 'battery-acid', name: 'Battery acid', minPH: 0, maxPH: 1, typicalPH: 0.5, note: 'Sulfuric acid, about 30–40 % (roughly 4–5 mol L⁻¹); corrosive.' },
  { id: 'lemon-juice', name: 'Lemon juice', minPH: 2, maxPH: 2.6, typicalPH: 2 },
  { id: 'vinegar', name: 'Vinegar', minPH: 2.5, maxPH: 3.5, typicalPH: 3 },
  { id: 'orange-juice', name: 'Orange juice', minPH: 3.3, maxPH: 4.2, typicalPH: 3.5 },
  { id: 'black-coffee', name: 'Black coffee', minPH: 4.8, maxPH: 5.2, typicalPH: 5 },
  { id: 'milk', name: 'Milk', minPH: 6.4, maxPH: 6.8, typicalPH: 6.6 },
  { id: 'pure-water', name: 'Pure water (25 °C)', minPH: 7, maxPH: 7, typicalPH: 7, note: 'pH 7 only at 25 °C. Pure water is always neutral ([H⁺] = [OH⁻]), but its pH changes with temperature because Kw changes.' },
  { id: 'blood', name: 'Human blood', minPH: 7.35, maxPH: 7.45, typicalPH: 7.4 },
  { id: 'seawater', name: 'Seawater', minPH: 7.8, maxPH: 8.3, typicalPH: 8 },
  { id: 'baking-soda', name: 'Baking soda solution', minPH: 8.2, maxPH: 8.4, typicalPH: 8.3 },
  { id: 'soap', name: 'Soapy water', minPH: 9, maxPH: 10, typicalPH: 9.5 },
  { id: 'ammonia-cleaner', name: 'Ammonia cleaner', minPH: 10.5, maxPH: 11.5, typicalPH: 11 },
  { id: 'bleach', name: 'Household bleach', minPH: 12, maxPH: 13, typicalPH: 12.5, note: 'Corrosive; never mix with acids or ammonia.' },
  { id: 'oven-cleaner', name: 'Oven cleaner', minPH: 13, maxPH: 14, typicalPH: 13.5, note: 'Contains sodium hydroxide; corrosive.' },
];

export const INDICATOR_BANDS: IndicatorBand[] = [
  { colour: 'red', label: 'Red', fromPH: 0, toPH: 3 },
  { colour: 'orange', label: 'Orange', fromPH: 3, toPH: 5 },
  { colour: 'yellow', label: 'Yellow', fromPH: 5, toPH: 6.5 },
  { colour: 'green', label: 'Green', fromPH: 6.5, toPH: 7.5 },
  { colour: 'blue', label: 'Blue', fromPH: 7.5, toPH: 11 },
  { colour: 'purple', label: 'Purple', fromPH: 11, toPH: 14 },
];

export function substanceById(id: string): PHSubstance {
  const substance = PH_SUBSTANCES.find((entry) => entry.id === id);
  if (!substance) throw new Error(`Unknown substance: ${id}`);
  return substance;
}

export function classifyPH(pH: number): PHClass {
  if (!Number.isFinite(pH) || pH < PH_MIN || pH > PH_MAX) {
    throw new Error(`pH must be between ${PH_MIN} and ${PH_MAX}`);
  }
  if (Math.abs(pH - NEUTRAL_PH) < NEUTRAL_TOLERANCE) return 'neutral';
  return pH < NEUTRAL_PH ? 'acidic' : 'basic';
}

export function indicatorBandFor(pH: number): IndicatorBand {
  classifyPH(pH);
  return (
    INDICATOR_BANDS.find((band) => pH >= band.fromPH && pH < band.toPH) ??
    INDICATOR_BANDS[INDICATOR_BANDS.length - 1]
  );
}

/** How many times greater [H⁺] is at pH a than at pH b. */
export function hydrogenIonRatio(pHa: number, pHb: number) {
  return PH_UNIT_FACTOR ** (pHb - pHa);
}

const SUPERSCRIPT_DIGITS = '⁰¹²³⁴⁵⁶⁷⁸⁹';

/** Superscript exponent for a whole number of pH units, e.g. 3 → ³. */
export function powerOfTen(units: number) {
  if (!Number.isInteger(units) || units < 0) throw new Error('Exponent must be a whole number');
  return [...String(units)].map((digit) => SUPERSCRIPT_DIGITS[Number(digit)]).join('');
}

export function formatTimes(value: number) {
  return `${Math.round(value).toLocaleString('en-AU')} times`;
}

// ---------------------------------------------------------------------------
// Neutralisation particle model
// ---------------------------------------------------------------------------

export interface NeutralisationState {
  hydrogenIons: number;
  hydroxideIons: number;
  waterFormed: number;
  classification: PHClass;
}

/** H⁺ + OH⁻ → H₂O, one-to-one, for a counted particle picture. */
export function neutralise(hydrogenIons: number, hydroxideIons: number): NeutralisationState {
  if (![hydrogenIons, hydroxideIons].every((count) => Number.isInteger(count) && count >= 0)) {
    throw new Error('Particle counts must be whole numbers of zero or more');
  }
  const waterFormed = Math.min(hydrogenIons, hydroxideIons);
  const remainingH = hydrogenIons - waterFormed;
  const remainingOH = hydroxideIons - waterFormed;
  return {
    hydrogenIons: remainingH,
    hydroxideIons: remainingOH,
    waterFormed,
    classification: remainingH > remainingOH ? 'acidic' : remainingOH > remainingH ? 'basic' : 'neutral',
  };
}

// ---------------------------------------------------------------------------
// Prediction questions — answers are computed from the data above
// ---------------------------------------------------------------------------

export type PHQuestionSpec =
  | { id: string; kind: 'classify'; substanceId: string }
  | { id: string; kind: 'more-acidic' | 'more-basic'; substanceIds: [string, string] }
  | { id: string; kind: 'factor'; substanceIds: [string, string] }
  | { id: string; kind: 'indicator'; substanceId: string }
  | { id: string; kind: 'neutralisation-product' };

export interface PHQuestionOption {
  value: string;
  label: string;
}

export interface PHQuestion {
  id: string;
  prompt: string;
  options: PHQuestionOption[];
  answer: string;
  explanation: string;
  /** Specific feedback for each wrong option. */
  wrongFeedback: Record<string, string>;
}

export const PH_QUESTION_SPECS: PHQuestionSpec[] = [
  { id: 'classify-lemon', kind: 'classify', substanceId: 'lemon-juice' },
  { id: 'classify-ammonia', kind: 'classify', substanceId: 'ammonia-cleaner' },
  { id: 'classify-water', kind: 'classify', substanceId: 'pure-water' },
  { id: 'classify-blood', kind: 'classify', substanceId: 'blood' },
  { id: 'acidic-vinegar-coffee', kind: 'more-acidic', substanceIds: ['black-coffee', 'vinegar'] },
  { id: 'basic-bleach-seawater', kind: 'more-basic', substanceIds: ['seawater', 'bleach'] },
  { id: 'factor-lemon-coffee', kind: 'factor', substanceIds: ['lemon-juice', 'black-coffee'] },
  { id: 'factor-vinegar-water', kind: 'factor', substanceIds: ['vinegar', 'pure-water'] },
  { id: 'indicator-soap', kind: 'indicator', substanceId: 'soap' },
  { id: 'indicator-lemon', kind: 'indicator', substanceId: 'lemon-juice' },
  { id: 'neutralisation-product', kind: 'neutralisation-product' },
];

const CLASS_LABELS: Record<PHClass, string> = { acidic: 'Acidic', neutral: 'Neutral', basic: 'Basic' };

const CLASS_WRONG_FEEDBACK: Record<PHClass, string> = {
  acidic: 'Acidic means pH below 7: more H⁺ than OH⁻.',
  neutral: 'Neutral is exactly pH 7 at 25 °C, where H⁺ and OH⁻ are equal.',
  basic: 'Basic means pH above 7: more OH⁻ than H⁺.',
};

function describePH(substance: PHSubstance) {
  return substance.minPH === substance.maxPH
    ? `pH ${substance.typicalPH}`
    : `typically pH ${substance.minPH}–${substance.maxPH}`;
}

export function buildQuestion(spec: PHQuestionSpec): PHQuestion {
  switch (spec.kind) {
    case 'classify': {
      const substance = substanceById(spec.substanceId);
      const answer = classifyPH(substance.typicalPH);
      const options = (['acidic', 'neutral', 'basic'] as PHClass[]).map((value) => ({ value, label: CLASS_LABELS[value] }));
      return {
        id: spec.id,
        prompt: `${substance.name} is ${describePH(substance)}. Is it acidic, neutral or basic?`,
        options,
        answer,
        explanation: `${substance.name} is ${answer}: ${CLASS_WRONG_FEEDBACK[answer]}`,
        wrongFeedback: Object.fromEntries(
          options
            .filter((option) => option.value !== answer)
            .map((option) => [
              option.value,
              `Check the number against 7. ${substance.name} is ${describePH(substance)}, so it is ${answer}, not ${option.value}.`,
            ]),
        ),
      };
    }
    case 'more-acidic':
    case 'more-basic': {
      const [first, second] = spec.substanceIds.map(substanceById);
      const wantAcid = spec.kind === 'more-acidic';
      const answerSubstance =
        wantAcid === first.typicalPH < second.typicalPH ? first : second;
      const other = answerSubstance === first ? second : first;
      return {
        id: spec.id,
        prompt: `Which is more ${wantAcid ? 'acidic' : 'basic'}: ${first.name} or ${second.name}?`,
        options: [first, second].map((substance) => ({ value: substance.id, label: substance.name })),
        answer: answerSubstance.id,
        explanation: `${answerSubstance.name} (${describePH(answerSubstance)}) is further ${wantAcid ? 'below' : 'above'} 7 than ${other.name} (${describePH(other)}).`,
        wrongFeedback: {
          [other.id]: wantAcid
            ? `A lower pH is more acidic. ${other.name} has the higher pH, so it is less acidic.`
            : `A higher pH is more basic. ${other.name} has the lower pH, so it is less basic.`,
        },
      };
    }
    case 'factor': {
      const [acidic, lessAcidic] = spec.substanceIds.map(substanceById);
      const units = lessAcidic.typicalPH - acidic.typicalPH;
      const ratio = hydrogenIonRatio(acidic.typicalPH, lessAcidic.typicalPH);
      const options = [units, units * PH_UNIT_FACTOR, ratio]
        .filter((value, index, values) => values.indexOf(value) === index)
        .map((value) => ({ value: String(value), label: formatTimes(value) }));
      return {
        id: spec.id,
        prompt: `${acidic.name} is about pH ${acidic.typicalPH}; ${lessAcidic.name} is about pH ${lessAcidic.typicalPH}. How many times more H⁺ does ${acidic.name.toLowerCase()} have?`,
        options,
        answer: String(ratio),
        explanation: `A difference of ${units} pH units is 10${powerOfTen(units)} = ${formatTimes(ratio)} more H⁺.`,
        wrongFeedback: Object.fromEntries(
          options
            .filter((option) => option.value !== String(ratio))
            .map((option) => [
              option.value,
              `The pH scale is not linear. Each unit is ×10, so ${units} units is 10${powerOfTen(units)}, not ${option.label}.`,
            ]),
        ),
      };
    }
    case 'indicator': {
      const substance = substanceById(spec.substanceId);
      const band = indicatorBandFor(substance.typicalPH);
      const options = INDICATOR_BANDS.map((entry) => ({ value: entry.colour, label: entry.label }));
      return {
        id: spec.id,
        prompt: `What colour would universal indicator typically turn in ${substance.name.toLowerCase()} (${describePH(substance)})?`,
        options,
        answer: band.colour,
        explanation: `pH ${substance.typicalPH} lies in the ${band.label.toLowerCase()} band (pH ${band.fromPH}–${band.toPH}).`,
        wrongFeedback: Object.fromEntries(
          options
            .filter((option) => option.value !== band.colour)
            .map((option) => {
              const chosen = INDICATOR_BANDS.find((entry) => entry.colour === option.value)!;
              return [
                option.value,
                `${chosen.label} is the band for pH ${chosen.fromPH}–${chosen.toPH}. Find pH ${substance.typicalPH} on the scale instead.`,
              ];
            }),
        ),
      };
    }
    case 'neutralisation-product':
    default:
      return {
        id: spec.id,
        prompt: 'In neutralisation, H⁺ ions from an acid meet OH⁻ ions from a base. What do they form?',
        options: [
          { value: 'water', label: 'Water, H₂O' },
          { value: 'hydrogen', label: 'Hydrogen gas, H₂' },
          { value: 'oxygen', label: 'Oxygen gas, O₂' },
        ],
        answer: 'water',
        explanation: 'H⁺ + OH⁻ → H₂O. The remaining ions form a salt, so acid + base → salt + water.',
        wrongFeedback: {
          hydrogen: 'Hydrogen gas forms when a reactive metal meets an acid, not when an acid meets a base.',
          oxygen: 'No oxygen gas forms. The H⁺ and OH⁻ ions join to make water.',
        },
      };
  }
}

export const PH_QUESTIONS: PHQuestion[] = PH_QUESTION_SPECS.map(buildQuestion);

export function checkPHAnswer(question: PHQuestion, value: string) {
  const option = question.options.find((entry) => entry.value === value);
  if (!option) throw new Error(`Unknown option ${value} for question ${question.id}`);
  const correct = value === question.answer;
  return {
    correct,
    feedback: correct ? question.explanation : `${question.wrongFeedback[value]} ${question.explanation}`,
  };
}
